import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { supabase } from '../../../lib/supabase';
import { paymentRecoveryService } from '../../../lib/paymentRecoveryService';

const DunningManagement = ({ failedPayments, onRefresh }) => {
  const [dunningNotifications, setDunningNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  useEffect(() => {
    loadDunningNotifications();
  }, [failedPayments]);

  const loadDunningNotifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('dunning_notifications')?.select(`
          *,
          failed_payment:failed_payments!failed_payment_id(
            *,
            user:user_profiles!user_id(id, full_name, email, phone)
          ),
          sms_log:sms_logs(id, status, twilio_message_sid)
        `)?.order('created_at', { ascending: false })?.limit(50);

      setDunningNotifications(data || []);
    } catch (error) {
      console.error('Error loading dunning notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (failedPaymentId, stage) => {
    if (!confirm(`Send ${stage} dunning notification?`)) return;

    setLoading(true);
    try {
      const { error } = await paymentRecoveryService?.sendDunningNotification(failedPaymentId, stage);

      if (!error) {
        alert('Dunning notification sent successfully');
        loadDunningNotifications();
        onRefresh?.();
      } else {
        alert('Failed to send notification');
      }
    } catch (error) {
      alert('Error sending notification');
    } finally {
      setLoading(false);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      initial: { bg: 'bg-blue-100', text: 'text-blue-800' },
      reminder_1: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      reminder_2: { bg: 'bg-orange-100', text: 'text-orange-800' },
      final_notice: { bg: 'bg-red-100', text: 'text-red-800' },
      suspended: { bg: 'bg-gray-100', text: 'text-gray-800' }
    };
    return colors?.[stage] || colors?.initial;
  };

  const getDeliveryStatusIcon = (status) => {
    if (status === 'sent') return { icon: 'CheckCircle', color: 'text-green-600' };
    if (status === 'failed') return { icon: 'XCircle', color: 'text-red-600' };
    return { icon: 'Clock', color: 'text-gray-600' };
  };

  const dunningTemplates = [
    {
      stage: 'initial',
      name: 'Initial Notification',
      description: 'First notification sent immediately after payment failure',
      icon: 'Mail'
    },
    {
      stage: 'reminder_1',
      name: 'First Reminder',
      description: 'Sent 24 hours after initial notification',
      icon: 'Bell'
    },
    {
      stage: 'reminder_2',
      name: 'Second Reminder',
      description: 'Sent 3 days after first reminder',
      icon: 'AlertTriangle'
    },
    {
      stage: 'final_notice',
      name: 'Final Notice',
      description: 'Sent 7 days after second reminder',
      icon: 'AlertOctagon'
    }
  ];

  const notificationStats = {
    total: dunningNotifications?.length || 0,
    sent: dunningNotifications?.filter(n => n?.delivery_status === 'sent')?.length || 0,
    opened: dunningNotifications?.filter(n => n?.opened_at)?.length || 0,
    responded: dunningNotifications?.filter(n => n?.response_received)?.length || 0
  };

  const responseRate = notificationStats?.sent > 0 
    ? ((notificationStats?.responded / notificationStats?.sent) * 100)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="Send" className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Total Sent</p>
              <p className="text-xl font-bold text-gray-900">{notificationStats?.sent}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="Eye" className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Opened</p>
              <p className="text-xl font-bold text-gray-900">{notificationStats?.opened}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="MessageCircle" className="w-5 h-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Responded</p>
              <p className="text-xl font-bold text-gray-900">{notificationStats?.responded}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="TrendingUp" className="w-5 h-5 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600">Response Rate</p>
              <p className="text-xl font-bold text-gray-900">{responseRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dunning Templates */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Dunning Message Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dunningTemplates?.map((template) => {
            const stageColor = getStageColor(template?.stage);
            return (
              <div key={template?.stage} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg ${stageColor?.bg} flex items-center justify-center`}>
                    <Icon name={template?.icon} className={`w-5 h-5 ${stageColor?.text}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{template?.name}</h4>
                    <p className="text-sm text-gray-600">{template?.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Notifications */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Dunning Notifications</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto" />
          </div>
        ) : dunningNotifications?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Icon name="Inbox" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No dunning notifications sent yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dunningNotifications?.map((notification) => {
              const stageColor = getStageColor(notification?.dunning_stage);
              const deliveryStatus = getDeliveryStatusIcon(notification?.delivery_status);

              return (
                <div key={notification?.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">
                          {notification?.failed_payment?.user?.full_name || 'Unknown Member'}
                        </h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${stageColor?.bg} ${stageColor?.text}`}>
                          {notification?.dunning_stage?.replace('_', ' ')?.toUpperCase()}
                        </span>
                        <Icon name={deliveryStatus?.icon} className={`w-4 h-4 ${deliveryStatus?.color}`} />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-gray-700">{notification?.message_sent}</p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Type</p>
                          <p className="font-semibold text-gray-900 capitalize">{notification?.notification_type}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Sent At</p>
                          <p className="font-semibold text-gray-900">
                            {notification?.sent_at 
                              ? new Date(notification?.sent_at)?.toLocaleString()
                              : 'Not sent'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Opened</p>
                          <p className="font-semibold text-gray-900">
                            {notification?.opened_at ? 'Yes' : 'No'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Response</p>
                          <p className="font-semibold text-gray-900">
                            {notification?.response_received ? 'Received' : 'Pending'}
                          </p>
                        </div>
                      </div>

                      {notification?.response_text && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm font-medium text-green-900 mb-1">Member Response:</p>
                          <p className="text-sm text-green-700">{notification?.response_text}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Integration Info */}
      <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
        <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
          <Icon name="Smartphone" className="w-5 h-5" />
          Twilio SMS Integration
        </h4>
        <p className="text-sm text-purple-800">
          All dunning notifications are sent via Twilio SMS with delivery tracking and response monitoring. 
          Messages are personalized with member name, amount, and payment details for maximum engagement.
        </p>
      </div>
    </div>
  );
};

export default DunningManagement;